import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "paymentElement",
    "paymentRequestButton",
    "submit",
    "buttonText",
    "spinner",
    "errorMessage",
    "deliveryFields"
  ]

  static values = {
    publishableKey: String,
    clientSecret: String,
    returnUrl: String,
    amount: Number,
    title: String,
    userName: String,
    userEmail: String
  }

  connect() {
    this.stripe = Stripe(this.publishableKeyValue)
    this.initializeStripe()
    this.setupEventListeners()
  }

  async initializeStripe() {
    // Initialize Stripe Elements first
    const options = {
      clientSecret: this.clientSecretValue,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#4F46E5',
        }
      }
    }

    // Create elements instance first
    this.elements = this.stripe.elements(options)

    // Set up payment request for Apple Pay with proper styling
    this.paymentRequest = this.stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: this.titleValue,
        amount: this.amountValue,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      merchantCapabilities: ['supports3DS'],
      supportedNetworks: ['mastercard', 'visa', 'amex']
    })

    // Check if the browser supports payment request
    const result = await this.paymentRequest.canMakePayment()
    console.log('Payment Request support check result:', result)

    if (result) {
      // Create payment request button using the existing elements instance
      const paymentRequestButtonElement = this.elements.create('paymentRequestButton', {
        paymentRequest: this.paymentRequest,
        style: {
          paymentRequestButton: {
            type: 'default',
            theme: 'dark',
            height: '44px'
          }
        }
      })

      try {
        await paymentRequestButtonElement.mount(this.paymentRequestButtonTarget)
        console.log('Payment Request Button mounted successfully')
      } catch (err) {
        console.error('Error mounting payment request button:', err)
        this.paymentRequestButtonTarget.style.display = 'none'
      }
    } else {
      console.log('Payment Request not supported')
      this.paymentRequestButtonTarget.style.display = 'none'
    }

    // Create and mount regular payment element
    this.paymentElement = this.elements.create('payment')
    await this.paymentElement.mount(this.paymentElementTarget)

    // Handle payment request events
    this.paymentRequest.on('paymentmethod', async (ev) => {
      try {
        const formData = this.getFormData()

        const { error } = await this.stripe.confirmPayment({
          clientSecret: this.clientSecretValue,
          confirmParams: {
            payment_method: ev.paymentMethod.id,
            return_url: this.returnUrlValue
          }
        });
        if (confirmError) {
          console.error('Confirmation error:', confirmError)
          ev.complete('fail')
          this.errorMessageTarget.textContent = confirmError.message
          return
        }

        ev.complete('success')

        if (paymentIntent.status === 'requires_action') {
          const { error } = await this.stripe.confirmCardPayment(this.clientSecretValue)
          if (error) {
            this.errorMessageTarget.textContent = error.message
            return
          }
        }

        window.location.href = this.returnUrlValue
      } catch (err) {
        console.error('Payment Request error:', err)
        ev.complete('fail')
        this.errorMessageTarget.textContent = 'An unexpected error occurred. Please try again.'
      }
    })
  }

  setupEventListeners() {
    this.formTarget.addEventListener('submit', (e) => this.handleSubmit(e))
  }

  async handleSubmit(event) {
    event.preventDefault()
    this.setLoading(true)

    const formData = this.getFormData()

    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: this.returnUrlValue,
        payment_method_data: {
          billing_details: {
            name: this.userNameValue,
            email: this.userEmailValue
          },
          metadata: formData
        }
      }
    })

    if (error) {
      this.errorMessageTarget.textContent = error.message
      this.setLoading(false)
    }
  }

  getFormData() {
    return {
      delivery_method: this.formTarget.querySelector('input[name="delivery_method"]:checked').value,
      delivery_address: this.formTarget.querySelector('textarea[name="delivery_address"]')?.value,
      delivery_date: this.formTarget.querySelector('input[name="delivery_date"]')?.value,
      special_instructions: this.formTarget.querySelector('textarea[name="special_instructions"]').value
    }
  }

  setLoading(isLoading) {
    this.submitTarget.disabled = isLoading
    this.buttonTextTarget.classList.toggle('hidden', isLoading)
    this.spinnerTarget.classList.toggle('hidden', !isLoading)
  }
}
